import type { ContentObjectOption, ContentObjectProps, IntersectFormState } from '@openx/types/targeting/contentObject';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyFilterMessage } from '../../../shared/EmptyFilterMessage/EmptyFilterMessage';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle';
import { SelectedItems } from '../../../shared/SelectedItems/SelectedItems';
import { Switch } from '../../../shared/Switch/Switch';
import type { TargetingItemsProps } from '../../../types';
import { itemTitle } from '../../constants';

export const IntersectItems = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  type,
  disabledReasonMessage,
}: TargetingItemsProps<ContentObjectProps> & { type: ContentObjectOption }) => {
  const { t } = useTranslation();

  const { op, val } = (targetingParams[type] as IntersectFormState) || {
    op: Intersect.INTERSECTS,
    val: new Set<string>(),
  };

  const typeLabel = op === Intersect.NOT_INTERSECTS ? t('blocked') : t('allowed');
  const filtersTitle = val.size ? typeLabel : '';
  const switchState = op === Intersect.NOT_INTERSECTS;
  const selectedItems = useMemo(() => [...val], [val]);

  const handleChange = useCallback(
    (values: string[]) => {
      const selected = new Set(values);
      const returnValue = values.length === 0 ? null : { op, val: selected };

      onChange((prevState: ContentObjectProps) => {
        return {
          ...prevState,
          [type]: returnValue,
        };
      });
    },
    [onChange, op, type]
  );

  if (val.size === 0) {
    return <EmptyFilterMessage text={t('No filters applied.')} />;
  }

  return (
    <div data-test="sub-section">
      <FiltersTitle
        onClear={() => handleChange([])}
        title={itemTitle(type, val.size)}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />

      {!readonly && (
        <Switch
          switchState={switchState}
          onSwitchChange={() =>
            onChange(prevState => ({
              ...prevState,
              [type]: {
                op: switchState ? Intersect.INTERSECTS : Intersect.NOT_INTERSECTS,
                val,
              },
            }))
          }
          switchOffLabel={t('allow')}
          switchOnLabel={t('block')}
          groupTitle={t('the following:')}
        />
      )}

      <SelectedItems
        filtersTitle={filtersTitle}
        isAddIcon={!switchState}
        selectedItems={selectedItems}
        readonly={readonly}
        handleChange={handleChange}
        dataTest={type}
      />
    </div>
  );
};
